import { clsx } from 'clsx'
import { type FC } from 'react'

import {
  type PimProductV11,
  PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../../global-config'
import { TENANT } from '../../../tenant'
import { priceAccessorShouldDisplayErxRecommendedRetailPrice } from '../../model/priceAccessor/priceAccessorShouldDisplayErxRecommendedRetailPrice'
import { priceAccessorShouldDisplayStrikeThroughPrice } from '../../model/priceAccessor/priceAccessorShouldDisplayStrikeThroughPrice'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import {
  type ProductPrices,
  useProductPrices,
} from '../ProductBuyBox/hooks/useProductPrices'

import { ProductListPrice } from './ProductListPrice/ProductListPrice'
import { ProductReferencePrice } from './ProductReferencePrice/ProductReferencePrice'

const isPriceStrikedThrough = ({
  displayPriceAmount,
  strikeThroughOfferPriceAmount,
}: {
  displayPriceAmount?: number
  strikeThroughOfferPriceAmount?: number
}): boolean => {
  return (
    !displayPriceAmount ||
    !strikeThroughOfferPriceAmount ||
    displayPriceAmount !== strikeThroughOfferPriceAmount
  )
}

const getOriginalPriceValue = ({
  erxRecommendedRetailPrice,
  isErxPriceDisplayed,
  shouldDisplayStrikeThroughPrice,
  strikeThroughOfferPrice,
}: {
  erxRecommendedRetailPrice: ProductPrices['erxRecommendedRetailPrice']
  isErxPriceDisplayed: boolean
  shouldDisplayStrikeThroughPrice: boolean
  strikeThroughOfferPrice: ProductPrices['strikeThroughOfferPrice']
}): number => {
  /**
   * WSAWA-4276
   * We show the AVP/UVP for ERX if there is no discount already present.
   * This is a failsafe for rare situations when we might have a discounted ERX product.
   * Even tough we've been told these products will never be discounted, it's better to have this scenario handled.
   */
  if (!shouldDisplayStrikeThroughPrice && isErxPriceDisplayed) {
    return erxRecommendedRetailPrice?.price ?? 0
  }

  return strikeThroughOfferPrice?.price ?? 0
}

export const ProductPriceOriginal: FC<{
  className?: string
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  offerId?: string | null
  product: PimProductV11
}> = ({ className, currentPrescriptionType, offerId, product }) => {
  const { tenant } = useGlobalConfigContext()

  const {
    displayPrice,
    erxRecommendedRetailPrice,
    isDiscountValid,
    isOfferMirakl,
    isThirtyDayPriceTrendDown,
    referencePriceType,
    shouldUseReferencePrice,
    showThirtyDaysPrice,
    strikeThroughOfferPrice,
    thirtyDaysLowestPrice,
  } = useProductPrices({
    currentPrescriptionType,
    offerId,
    product,
  })

  if (
    !displayPrice ||
    currentPrescriptionType ===
      PrescriptionPrescriptionTypeEnum.PrivatePrescription
  ) {
    return null
  }

  const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)

  /*
   * WSAWA-4276
   * We show AVP/UVP price for ERX products that are either Free Prescription or Zuzahlungsbefreiung.
   * For Private Insurance/Privatrezept we don't need to show AVP/UVP since the price is the recommended retail price.
   */
  const isErxPriceDisplayed =
    priceAccessorShouldDisplayErxRecommendedRetailPrice({
      currentPrescriptionType,
      erxRecommendedRetailPrice,
      isPrescriptionProduct,
    })

  const originalPriceToShow =
    showThirtyDaysPrice && isThirtyDayPriceTrendDown
      ? (thirtyDaysLowestPrice?.amount ?? 0)
      : getOriginalPriceValue({
          erxRecommendedRetailPrice,
          isErxPriceDisplayed,
          shouldDisplayStrikeThroughPrice:
            priceAccessorShouldDisplayStrikeThroughPrice({
              currentPrescriptionType,
              displayPrice,
              isDiscountValid,
              strikeThroughOfferPrice,
            }),
          strikeThroughOfferPrice,
        })
  const priceToShow = shouldUseReferencePrice
    ? (strikeThroughOfferPrice?.price ?? 0)
    : originalPriceToShow

  // We don't show anything if the original price is lower that the displayed price
  if (priceToShow <= (displayPrice.price ?? 0)) {
    return null
  }

  if (shouldUseReferencePrice) {
    return (
      <ProductReferencePrice
        className={clsx('text-s text-dark-primary-strong', className)}
        priceToShow={priceToShow}
        referencePriceType={referencePriceType}
      />
    )
  }

  // For legal reasons, we may not visually strike through the price on Belgian own-stock products.
  const isBelgian1P = tenant === TENANT.BE && !isOfferMirakl

  const shouldVisuallyStrikeThroughPrice =
    (!showThirtyDaysPrice || !isThirtyDayPriceTrendDown) &&
    isPriceStrikedThrough({
      displayPriceAmount: displayPrice.price,
      strikeThroughOfferPriceAmount: strikeThroughOfferPrice?.price,
    }) &&
    !isErxPriceDisplayed &&
    !isBelgian1P

  return (
    <ProductListPrice
      className={clsx('flex gap-x-1', className)}
      isOfferMirakl={isOfferMirakl}
      isPrescriptionProduct={isPrescriptionProduct}
      isThirtyDayPriceTrendDown={isThirtyDayPriceTrendDown}
      priceToShow={priceToShow}
      shouldVisuallyStrikeThroughPrice={shouldVisuallyStrikeThroughPrice}
      showThirtyDaysPrice={showThirtyDaysPrice}
    />
  )
}
